import * as React from 'react';
import {Helmet} from 'react-helmet';

import {headTitle} from 'shells/document/head-title';

import styles from './debug-page-header.module.css';

type Props = {
    title: string;
    subtitle: string;
};

function MatrixRain(): React.ReactElement {
    const columns = 20;
    const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノ{}[]<>/\\';

    return (
        <div className={styles['matrix-container']}>
            {Array.from({length: columns}).map((_, i) => (
                <div
                    key={i}
                    className={styles['matrix-column']}
                    style={{
                        left: `${(i / columns) * 100}%`,
                        animationDelay: `${Math.random() * 5}s`,
                        animationDuration: `${8 + Math.random() * 8}s`,
                    }}
                >
                    {Array.from({length: 15}).map((_, j) => (
                        <span key={j} style={{opacity: 0.1 + Math.random() * 0.3}}>
                            {chars[Math.floor(Math.random() * chars.length)]}
                        </span>
                    ))}
                </div>
            ))}
        </div>
    );
}

function DebugIllustration(): React.ReactElement {
    return (
        <svg
            width='280'
            height='200'
            viewBox='0 0 280 200'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
        >
            {/* Keyboard */}
            <rect x='20' y='140' width='160' height='50' rx='6' fill='#2d3748' />
            <rect x='28' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='44' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='60' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='76' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='92' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='108' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='124' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='140' y='148' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='156' y='148' width='16' height='10' rx='2' fill='#4a5568' />
            <rect x='28' y='162' width='16' height='10' rx='2' fill='#4a5568' />
            <rect x='48' y='162' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='64' y='162' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='80' y='162' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='96' y='162' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='112' y='162' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='128' y='162' width='12' height='10' rx='2' fill='#4a5568' />
            <rect x='144' y='162' width='28' height='10' rx='2' fill='#4a5568' />
            <rect x='36' y='176' width='80' height='8' rx='2' fill='#4a5568' />

            {/* Monitor/Screen */}
            <rect x='140' y='30' width='120' height='90' rx='4' fill='#1a202c' />
            <rect x='146' y='36' width='108' height='72' rx='2' fill='#0d1117' />
            <rect x='185' y='120' width='40' height='8' fill='#2d3748' />
            <rect x='175' y='128' width='60' height='6' rx='2' fill='#4a5568' />

            {/* Code lines on screen */}
            <rect x='152' y='44' width='40' height='4' rx='1' fill='#f472b6' opacity='0.8' />
            <rect x='152' y='52' width='60' height='4' rx='1' fill='#60a5fa' opacity='0.8' />
            <rect x='160' y='60' width='50' height='4' rx='1' fill='#a78bfa' opacity='0.8' />
            <rect x='160' y='68' width='35' height='4' rx='1' fill='#34d399' opacity='0.8' />
            <rect x='152' y='76' width='25' height='4' rx='1' fill='#f472b6' opacity='0.8' />
            <rect x='152' y='84' width='55' height='4' rx='1' fill='#fbbf24' opacity='0.8' />
            <rect x='160' y='92' width='45' height='4' rx='1' fill='#60a5fa' opacity='0.8' />

            {/* Bug character */}
            <ellipse cx='70' cy='70' rx='28' ry='22' fill='#dc2626' />
            <ellipse cx='70' cy='70' rx='24' ry='18' fill='#ef4444' />
            {/* Bug spots */}
            <circle cx='60' cy='65' r='4' fill='#b91c1c' />
            <circle cx='75' cy='62' r='3' fill='#b91c1c' />
            <circle cx='68' cy='75' r='3.5' fill='#b91c1c' />
            <circle cx='82' cy='72' r='2.5' fill='#b91c1c' />
            {/* Bug head */}
            <circle cx='70' cy='48' r='10' fill='#dc2626' />
            {/* Bug eyes */}
            <circle cx='66' cy='46' r='4' fill='white' />
            <circle cx='74' cy='46' r='4' fill='white' />
            <circle cx='66' cy='47' r='2' fill='#1a202c' />
            <circle cx='74' cy='47' r='2' fill='#1a202c' />
            {/* Bug antennae */}
            <path d='M62 40 Q58 30 52 28' stroke='#dc2626' strokeWidth='2' fill='none' />
            <path d='M78 40 Q82 30 88 28' stroke='#dc2626' strokeWidth='2' fill='none' />
            <circle cx='52' cy='28' r='3' fill='#fbbf24' />
            <circle cx='88' cy='28' r='3' fill='#fbbf24' />
            {/* Bug legs */}
            <path d='M45 60 Q35 55 30 50' stroke='#dc2626' strokeWidth='2.5' fill='none' />
            <path d='M45 70 Q32 70 25 68' stroke='#dc2626' strokeWidth='2.5' fill='none' />
            <path d='M45 80 Q35 85 30 92' stroke='#dc2626' strokeWidth='2.5' fill='none' />
            <path d='M95 60 Q105 55 110 50' stroke='#dc2626' strokeWidth='2.5' fill='none' />
            <path d='M95 70 Q108 70 115 68' stroke='#dc2626' strokeWidth='2.5' fill='none' />
            <path d='M95 80 Q105 85 110 92' stroke='#dc2626' strokeWidth='2.5' fill='none' />

            {/* Magnifying glass */}
            <circle
                cx='230'
                cy='75'
                r='22'
                stroke='#f59e0b'
                strokeWidth='4'
                fill='rgba(251, 191, 36, 0.1)'
            />
            <path d='M248 93 L265 110' stroke='#f59e0b' strokeWidth='6' strokeLinecap='round' />
            {/* Search lines inside magnifier */}
            <path d='M218 70 L242 70' stroke='#f59e0b' strokeWidth='2' opacity='0.5' />
            <path d='M220 78 L240 78' stroke='#f59e0b' strokeWidth='2' opacity='0.5' />
        </svg>
    );
}

export function DebugPageHeader(props: Props): React.ReactElement {
    return (
        <>
            <Helmet>
                <title>{headTitle(props.title)}</title>
            </Helmet>
            <div className={styles['header-container']}>
                <MatrixRain />
                <div className={styles['header-content']}>
                    <div className={styles['text-section']}>
                        <div className={styles['debug-badge']}>Internal Debug</div>
                        <h1 className={styles.title}>{props.title}</h1>
                        <p className={styles.subtitle}>{props.subtitle}</p>
                    </div>
                    <div className={styles['illustration-section']}>
                        <DebugIllustration />
                    </div>
                </div>
            </div>
        </>
    );
}
